import React from 'react';
import gql from 'graphql-tag';
import { useQuery, ApolloProvider } from '@apollo/client';
import client from '../lib/apolloClient';
import ModernMessagingWrapper from '../components/Succession/Messaging/FeatureFlagWrapper/ModernMessagingWrapper';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      name
      is_admin
      allowed_name
      profile_picture
      company {
        id
        name
        features
      }
    }
  }
`;

// Inner component that uses the hook
const MessagingWithUser = (props) => {
  const { loading, error, data } = useQuery(GET_CURRENT_USER);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading messaging...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to load messaging</h3>
          <p className="text-gray-600 text-sm mb-4">{error.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <ModernMessagingWrapper
      currentUser={data.getCurrentUser}
      {...props}
    />
  );
};

const ModernMessagingContainer = props => {
  return (
    <ApolloProvider client={client}>
      <div 
        className="messaging-app" 
        style={{ 
          height: '100%',
          width: '100%',
          backgroundColor: 'white',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontSize: '14px',
          lineHeight: '1.5',
          color: '#111827',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <style>
          {`
            .messaging-app * {
              box-sizing: border-box;
            }
            .messaging-app .w-8 { width: 2rem !important; }
            .messaging-app .h-8 { height: 2rem !important; }
            .messaging-app .w-16 { width: 4rem !important; }
            .messaging-app .h-16 { height: 4rem !important; }
            .messaging-app .text-sm { font-size: 0.875rem !important; }
            .messaging-app .text-lg { font-size: 1.125rem !important; }
            .messaging-app .flex { display: flex !important; }
            .messaging-app .items-center { align-items: center !important; }
            .messaging-app .justify-center { justify-content: center !important; }
            .messaging-app .h-full { height: 100% !important; }
            .messaging-app .bg-gray-50 { background-color: #f9fafb !important; }
            .messaging-app .text-center { text-align: center !important; }
            .messaging-app .border-2 { border-width: 2px !important; }
            .messaging-app .border-gray-200 { border-color: #e5e7eb !important; }
            .messaging-app .border-t-blue-500 { border-top-color: #3b82f6 !important; }
            .messaging-app .rounded-full { border-radius: 9999px !important; }
            .messaging-app .animate-spin { animation: spin 1s linear infinite !important; }
            .messaging-app .mx-auto { margin-left: auto !important; margin-right: auto !important; }
            .messaging-app .mb-4 { margin-bottom: 1rem !important; }
            .messaging-app .text-gray-600 { color: #4b5563 !important; }
            .messaging-app .bg-red-100 { background-color: #fee2e2 !important; }
            .messaging-app .text-red-500 { color: #ef4444 !important; }
            .messaging-app .text-gray-900 { color: #111827 !important; }
            .messaging-app .font-semibold { font-weight: 600 !important; }
            .messaging-app .mb-2 { margin-bottom: 0.5rem !important; }
            .messaging-app .max-w-md { max-width: 28rem !important; }
            .messaging-app .bg-blue-500 { background-color: #3b82f6 !important; }
            .messaging-app .text-white { color: #ffffff !important; }
            .messaging-app .px-4 { padding-left: 1rem !important; padding-right: 1rem !important; }
            .messaging-app .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
            .messaging-app .rounded-lg { border-radius: 0.5rem !important; }
            .messaging-app .hover\\:bg-blue-600:hover { background-color: #2563eb !important; }
            .messaging-app .transition-colors { transition-property: color, background-color, border-color !important; transition-duration: 150ms !important; }
            
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
        <MessagingWithUser {...props} />
      </div>
    </ApolloProvider>
  );
};

export default ModernMessagingContainer; 